import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

/**
 * workerAssignmentsService — qui travaille sur quel chantier, quel jour, a quelles heures.
 *
 * Une ligne = une personne, un chantier, un jour, une PLAGE HORAIRE. Il n'y a plus
 * de bloc am/pm/journee : tout est start_time -> end_time, tous deux NOT NULL.
 *
 * La base porte desormais une contrainte EXCLUDE, worker_assignments_no_overlap :
 * deux plages qui se RECOUPENT pour le meme worker_id sont refusees par Postgres.
 * Sa violation rend le code 23P01 — un conflit d'exclusion, pas un doublon d'index.
 *
 * 🔴 PIEGES DE CE DEPOT, respectes ici :
 *   - supabase.from() rend un PromiseLike SANS .catch() : on await dans try/catch.
 *   - un DELETE qui ne touche aucune ligne renvoie un SUCCES : on demande donc les
 *     lignes supprimees pour savoir si quelque chose a bouge.
 *   - toCamelCase ne camelise que le premier niveau : ce service ne joint donc
 *     rien et laisse la page resoudre les noms depuis ses propres listes.
 */

/** Code Postgres d'une violation de contrainte EXCLUDE. Ce n'est PAS 23505. */
export const OVERLAP_ERROR_CODE = '23P01';

// Aucune jointure : le nom du travailleur et le numero de projet sont resolus par
// la page, qui charge deja les deux listes. C'est le motif de directiveMap dans
// ExtrasPage — une carte en memoire plutot qu'une seconde requete, et surtout
// aucune dependance au nom exact des contraintes FK, que PostgREST exige des que
// deux colonnes pointent vers la meme table (worker_id et created_by, ici).
const ASSIGNMENT_SELECT =
  'id, organization_id, worker_id, project_id, work_date, start_time, end_time, note, created_by, created_at';

/** Aplatit une ligne en camelCase. Aucun objet imbrique a traverser. */
const mapAssignment = (row) => ({
  id: row?.id,
  organizationId: row?.organization_id,
  workerId: row?.worker_id,
  projectId: row?.project_id,
  workDate: row?.work_date,
  startTime: row?.start_time,
  endTime: row?.end_time,
  note: row?.note,
  createdBy: row?.created_by,
  createdAt: row?.created_at,
});

/**
 * Toutes les assignations de l'organisation sur une plage de dates.
 * @param {string} fromDate  date nue, AAAA-MM-JJ, incluse
 * @param {string} toDate    date nue, AAAA-MM-JJ, incluse
 */
export const listAssignments = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return [];
  try {
    const { data, error } = await supabase
      ?.from('worker_assignments')
      ?.select(ASSIGNMENT_SELECT)
      ?.gte('work_date', fromDate)
      ?.lte('work_date', toDate)
      ?.order('work_date', { ascending: true });

    if (error) {
      console.error('[workerAssignments] listAssignments:', error?.message);
      return [];
    }
    return (data || [])?.map(mapAssignment);
  } catch (err) {
    console.error('[workerAssignments] listAssignments:', err);
    return [];
  }
};

/** Les assignations d'UNE personne sur une plage — pour detecter les conflits. */
export const listWorkerAssignments = async (workerId, fromDate, toDate) => {
  if (!workerId || !fromDate || !toDate) return [];
  try {
    const { data, error } = await supabase
      ?.from('worker_assignments')
      ?.select(ASSIGNMENT_SELECT)
      ?.eq('worker_id', workerId)
      ?.gte('work_date', fromDate)
      ?.lte('work_date', toDate);

    if (error) {
      console.error('[workerAssignments] listWorkerAssignments:', error?.message);
      return [];
    }
    return (data || [])?.map(mapAssignment);
  } catch (err) {
    console.error('[workerAssignments] listWorkerAssignments:', err);
    return [];
  }
};

/** La ligne a inserer, dans la forme attendue par la base. */
const toRow = (orgId, userId, { workerId, projectId, workDate, startTime, endTime, note }) => ({
  organization_id: orgId,
  worker_id: workerId,
  project_id: projectId,
  work_date: workDate,
  // start_time et end_time sont NOT NULL : ils partent TOUJOURS, dans tous les cas.
  start_time: startTime,
  end_time: endTime,
  note: note?.trim() || null,
  created_by: userId || null,
});

/** Cree une assignation. */
export const createAssignment = async (payload) => {
  const { data, error } = await createAssignments([payload]);
  return { data: data?.[0] || null, error };
};

/**
 * Cree PLUSIEURS assignations en un seul appel.
 *
 * Utilise par le mode « Plusieurs jours ». L'appelant a deja retire les journees en
 * conflit ; ce qui reste ici est le filet de securite, pour le cas ou deux personnes
 * saisiraient en meme temps : la contrainte EXCLUDE rejette alors tout le lot.
 */
export const createAssignments = async (payloads) => {
  if (!payloads?.length) return { data: [], error: null };
  try {
    const orgId = await getCurrentOrgId();
    if (!orgId) return { data: [], error: 'no-org' };

    const { data: { user } } = await supabase?.auth?.getUser();
    const rows = payloads?.map((p) => toRow(orgId, user?.id, p));

    const { data, error } = await supabase
      ?.from('worker_assignments')
      ?.insert(rows)
      ?.select(ASSIGNMENT_SELECT);

    if (error) return { data: [], error };
    return { data: (data || [])?.map(mapAssignment), error: null };
  } catch (err) {
    console.error('[workerAssignments] createAssignments:', err);
    return { data: [], error: err };
  }
};

/** Modifie une assignation. */
export const updateAssignment = async (id, { workerId, projectId, workDate, startTime, endTime, note }) => {
  if (!id) return { data: null, error: 'no-id' };
  try {
    const { data, error } = await supabase
      ?.from('worker_assignments')
      ?.update({
        worker_id: workerId,
        project_id: projectId,
        work_date: workDate,
        start_time: startTime,
        end_time: endTime,
        note: note?.trim() || null,
      })
      ?.eq('id', id)
      ?.select(ASSIGNMENT_SELECT)
      ?.single();

    if (error) return { data: null, error };
    return { data: mapAssignment(data), error: null };
  } catch (err) {
    console.error('[workerAssignments] updateAssignment:', err);
    return { data: null, error: err };
  }
};

/**
 * Supprime une assignation.
 *
 * 🔴 Un DELETE PostgREST qui ne touche aucune ligne renvoie un succes — une ligne
 * masquee par la RLS disparaitrait donc « avec succes » sans jamais avoir bouge.
 * On demande les lignes supprimees et on compte.
 */
export const deleteAssignment = async (id) => {
  if (!id) return { deleted: 0, error: 'no-id' };
  try {
    const { data, error } = await supabase
      ?.from('worker_assignments')
      ?.delete()
      ?.eq('id', id)
      ?.select('id');

    if (error) return { deleted: 0, error };
    return { deleted: (data || [])?.length, error: null };
  } catch (err) {
    console.error('[workerAssignments] deleteAssignment:', err);
    return { deleted: 0, error: err };
  }
};

/** « 07:00:00 » ou « 07:00 » -> minutes depuis minuit. null si illisible. */
export const toMinutes = (time) => {
  if (!time) return null;
  const [h, m] = String(time)?.split(':');
  const hh = parseInt(h, 10);
  const mm = parseInt(m, 10);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
};

/** Minutes depuis minuit -> « 07:00:00 », la forme attendue par une colonne time. */
export const toTimeString = (minutes) => {
  const m = Math.max(0, Math.round(minutes));
  const hh = String(Math.floor(m / 60))?.padStart(2, '0');
  const mm = String(m % 60)?.padStart(2, '0');
  return `${hh}:${mm}:00`;
};

/**
 * 🔴 LES CHEVAUCHEMENTS — DOUBLEE COTE APPLICATION, ET C'EST VOULU.
 *
 * La base porte bien une contrainte EXCLUDE qui refuse deux plages se recoupant
 * pour la meme personne. Mais elle refuse le LOT ENTIER : en mode « Plusieurs
 * jours », une seule journee en conflit ferait echouer les dix. Cette fonction
 * sert a ecarter les journees fautives AVANT l'insertion, pour que les autres
 * passent — et pour nommer a l'utilisateur celles qui ont saute.
 *
 * NE LA SUPPRIMEZ PAS en croyant qu'elle double la contrainte : la contrainte est
 * le filet, celle-ci est le tri.
 *
 * Deux plages qui se touchent bout a bout — 7h-11h puis 11h-15h — ne se recoupent
 * PAS : c'est une journee coupee, un cas legitime sur un chantier. Le test est
 * donc strict des deux cotes.
 *
 * @param {Array}  existing   assignations deja connues de la MEME personne
 * @param {Object} candidate  { workDate, startTime, endTime }
 * @param {string} ignoreId   l'assignation en cours de modification, a exclure
 * @returns {Array} les assignations en conflit, vide si aucune
 */
export const findConflicts = (existing, candidate, ignoreId = null) => {
  const { workDate, startTime, endTime } = candidate || {};
  if (!workDate || !startTime || !endTime) return [];

  const bStart = toMinutes(startTime);
  const bEnd = toMinutes(endTime);
  if (bStart == null || bEnd == null) return [];

  return (existing || [])?.filter((a) => {
    if (a?.workDate !== workDate || a?.id === ignoreId) return false;
    const aStart = toMinutes(a?.startTime);
    const aEnd = toMinutes(a?.endTime);
    if (aStart == null || aEnd == null) return false;
    return bStart < aEnd && aStart < bEnd;
  });
};

export default {
  listAssignments,
  listWorkerAssignments,
  createAssignment,
  createAssignments,
  updateAssignment,
  deleteAssignment,
  findConflicts,
  toMinutes,
  toTimeString,
  OVERLAP_ERROR_CODE,
};
